<script lang="ts">
  import { onMount, onDestroy } from "svelte";

  interface Props {
    content: string;
    filePath: string;
    onSave: (content: string) => void;
  }

  let { content, filePath, onSave }: Props = $props();

  let editorEl: HTMLDivElement;
  let editorView: any;
  let saving = $state(false);

  onMount(() => { initEditor(); });
  onDestroy(() => { editorView?.destroy(); });

  async function initEditor() {
    const { EditorView, basicSetup } = await import("codemirror");
    const { EditorState } = await import("@codemirror/state");
    const { oneDark } = await import("@codemirror/theme-one-dark");
    const { keymap } = await import("@codemirror/view");

    const saveKeymap = keymap.of([
      {
        key: "Mod-s",
        run: () => {
          handleSave();
          return true;
        },
      },
    ]);

    const state = EditorState.create({
      doc: content,
      extensions: [basicSetup, oneDark, saveKeymap],
    });

    editorView = new EditorView({
      state,
      parent: editorEl,
    });
  }

  function handleSave() {
    if (!editorView) return;
    saving = true;
    const doc = editorView.state.doc.toString();
    onSave(doc);
    saving = false;
  }
</script>

<div class="editor">
  <div class="editor-toolbar">
    <button class="btn-save" onclick={handleSave} disabled={saving}>
      {saving ? "Saving..." : "Save"} <kbd>Ctrl+S</kbd>
    </button>
  </div>
  <div class="editor-area" bind:this={editorEl}></div>
</div>

<style>
  .editor {
    display: flex;
    flex-direction: column;
    height: 460px;
  }

  .editor-toolbar {
    display: flex;
    justify-content: flex-end;
    padding: 0.4rem 0.75rem;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
  }

  .btn-save {
    padding: 0.25rem 0.75rem;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .btn-save:hover:not(:disabled) { background: var(--accent-hover); }
  .btn-save:disabled { opacity: 0.5; cursor: not-allowed; }

  .btn-save kbd {
    font-size: 0.65rem;
    opacity: 0.7;
    font-family: var(--font-mono);
  }

  .editor-area {
    flex: 1;
    overflow: auto;
  }

  :global(.cm-editor) {
    height: 100%;
  }
</style>
